import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Loader2 } from "lucide-react";
import AuthShell from "../components/AuthShell";
import { useAuth, maskEmail } from "../context/AuthContext";

const LEN = 6;

export default function VerifyCode() {
  const { pendingUser, pendingEmail, verifyCode, sendCode } = useAuth();
  const emailToVerify = pendingUser?.email || pendingEmail;
  const navigate = useNavigate();
  const [digits, setDigits] = useState(Array(LEN).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendState, setResendState] = useState("idle"); // idle | sending | sent
  const inputsRef = useRef([]);

  useEffect(() => {
    if (!emailToVerify) {
      navigate("/signin", { replace: true });
    }
  }, [emailToVerify, navigate]);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const setAt = (idx, value) => {
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const onChange = (idx) => (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) {
      setAt(idx, "");
      return;
    }
    // If the user pastes or types multiple chars, spread them across inputs.
    if (raw.length > 1) {
      const chars = raw.slice(0, LEN - idx).split("");
      setDigits((prev) => {
        const next = [...prev];
        chars.forEach((c, i) => (next[idx + i] = c));
        return next;
      });
      const nextIdx = Math.min(idx + chars.length, LEN - 1);
      inputsRef.current[nextIdx]?.focus();
      return;
    }
    setAt(idx, raw);
    if (idx < LEN - 1) inputsRef.current[idx + 1]?.focus();
  };

  const onKeyDown = (idx) => (e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const onPaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "");
    if (!text) return;
    e.preventDefault();
    const chars = text.slice(0, LEN).split("");
    setDigits(() => {
      const next = Array(LEN).fill("");
      chars.forEach((c, i) => (next[i] = c));
      return next;
    });
    const nextIdx = Math.min(chars.length, LEN - 1);
    inputsRef.current[nextIdx]?.focus();
  };

  const onSubmit = async (e) => {
    e?.preventDefault();
    const code = digits.join("");
    if (code.length !== LEN) {
      setError("Please enter the full 6-digit code");
      return;
    }
    setError("");
    setLoading(true);
    const result = await verifyCode(code);
    setLoading(false);
    if (result.ok) {
      navigate("/app/dashboard", { replace: true });
    } else {
      setError(result.error || "Invalid code, please try again");
      setDigits(Array(LEN).fill(""));
      inputsRef.current[0]?.focus();
    }
  };

  const onResend = async () => {
    if (!emailToVerify) return;
    setResendState("sending");
    await sendCode(emailToVerify);
    setResendState("sent");
    setTimeout(() => setResendState("idle"), 2500);
  };

  const onChangeEmail = () => {
    navigate("/signin");
  };

  if (!emailToVerify) return null;

  return (
    <AuthShell
      title="Verify your email"
      subtitle={
        <>
          We’ve sent a 6-digit code to{" "}
          <span className="font-medium text-gray-700">
            {maskEmail(emailToVerify)}
          </span>
        </>
      }
    >
      <form onSubmit={onSubmit}>
        <div className="mb-4 flex items-center gap-2 text-xs text-gray-500">
          <Shield className="h-4 w-4 text-indigo-600" />
          Two-factor verification keeps your account secure
        </div>
        <div
          className="flex items-center justify-between gap-1.5 sm:gap-2"
          onPaste={onPaste}
        >
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputsRef.current[i] = el)}
              inputMode="numeric"
              maxLength={LEN}
              value={d}
              onChange={onChange(i)}
              onKeyDown={onKeyDown(i)}
              className="h-10 w-10 rounded-lg border border-gray-300 bg-white text-center text-lg font-semibold text-gray-900 outline-none transition-colors duration-150 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 sm:h-12 sm:w-12"
              aria-label={`Digit ${i + 1}`}
            />
          ))}
        </div>
        {error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <button type="submit" className="btn-primary mt-6 w-full" disabled={loading}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Verify code
        </button>
      </form>
      <div className="mt-5 flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={onResend}
          className="font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-60"
          disabled={resendState !== "idle"}
        >
          {resendState === "idle" && "Resend code"}
          {resendState === "sending" && "Sending…"}
          {resendState === "sent" && "Code sent"}
        </button>
        <button
          type="button"
          onClick={onChangeEmail}
          className="font-medium text-gray-600 hover:text-gray-900"
        >
          Change email
        </button>
      </div>
    </AuthShell>
  );
}
